import React, { useRef, memo, useState, useMemo } from "react";
import { TextField, Tooltip } from "@mui/material";
import { useVariableTooltip } from "hooks/useVariableTooltip";
import { DialogVariableTooltip } from "./DialogVariableTooltip";
import { connect } from "react-redux";

const TextFieldDynamicVariableTooltip = memo(({
    showContextMenu = true,
    InputProps,
    responseVariables,
    ...props
}) => {
    const textFieldRef = useRef(null);
    const [isMouseOverTooltip, setIsMouseOverTooltip] = useState(false);
    const closeTimeoutRef = useRef(null);
    const [tooltipVisible, setTooltipVisible] = useState(false);

    const variableSources = useMemo(() => {
        return {
            apiResponseVariables: {
                current_response_body: {
                    ...responseVariables
                }
            } || {}
        };
    }, [responseVariables]);

    const {
        handleMouseMove,
        dialogOpen,
        dialogPosition,
        selectedVariable,
        handleClose,
        setVariableKey,
        setVariableValue,
        variableKey,
        variableValue,
    } = useVariableTooltip(
        textFieldRef,
        variableSources
    );

    const handleTextFieldMouseMove = (e) => {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
        }

        // Get the actual input element
        const input = e.target.querySelector('input') || e.target;
        const value = input?.value;
        if (!value) return;

        // Get the correct bounding rect
        const rect = input.getBoundingClientRect();
        const x = e.clientX - rect.left;

        const variableMatches = [...value.matchAll(/{{(.*?)}}/g)];
        let currentVariable = null;
        let isOverVariable = false;

        for (const match of variableMatches) {
            const [fullMatch, extractedVar] = match;
            const startIndex = value.indexOf(fullMatch, match.index);
            const endIndex = startIndex + fullMatch.length;

            const tempSpan = document.createElement('span');
            tempSpan.style.visibility = 'hidden';
            tempSpan.style.position = 'absolute';
            tempSpan.style.whiteSpace = 'pre';
            tempSpan.style.font = window.getComputedStyle(input).font;
            document.body.appendChild(tempSpan);

            tempSpan.textContent = value.substring(0, startIndex);
            const startX = tempSpan.offsetWidth;

            tempSpan.textContent = fullMatch;
            const varWidth = tempSpan.offsetWidth;
            const endX = startX + varWidth;

            document.body.removeChild(tempSpan);

            if (x >= startX - 5 && x <= endX + 5) {
                currentVariable = extractedVar.trim();
                isOverVariable = true;
                break;
            }
        }

        if (currentVariable && isOverVariable) {
            setTooltipVisible(true);
            handleMouseMove({
                clientX: e.clientX,
                clientY: e.clientY,
                variableName: currentVariable,
                event: e,
            });
        } else {
            setTooltipVisible(false);
            handleClose();
        }
    };

    const handleMouseLeave = () => {
        if (!isMouseOverTooltip) {
            closeTimeoutRef.current = setTimeout(() => {
                setTooltipVisible(false);
                handleClose();
            }, 100);
        }
    };

    const handleTooltipMouseEnter = () => {
        setIsMouseOverTooltip(true);
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
        }
    };

    const handleTooltipMouseLeave = () => {
        setIsMouseOverTooltip(false);
        setTooltipVisible(false);
        handleClose();
    };

    return (
        <div style={{ position: 'relative', width: '100%' }}>
            <TextField
                {...props}
                inputRef={textFieldRef}
                fullWidth
                InputProps={{
                    ...InputProps,
                    onMouseMove: (e) => {
                        InputProps?.onMouseMove?.(e);
                        handleTextFieldMouseMove(e);
                    },
                    onMouseLeave: (e) => {
                        InputProps?.onMouseLeave?.(e);
                        handleMouseLeave();
                    },
                    style: {
                        ...InputProps?.style,
                    },
                }}
            />
            {dialogOpen && (
                <div
                    style={{
                        position: 'fixed',
                        left: dialogPosition.left,
                        top: dialogPosition.top,
                        zIndex: 9999,
                    }}
                >
                    <Tooltip
                        open={tooltipVisible}
                        placement="bottom"
                        onMouseEnter={handleTooltipMouseEnter}
                        onMouseLeave={handleTooltipMouseLeave}
                        PopperProps={{
                            sx: {
                                '& .MuiTooltip-tooltip': {
                                    backgroundColor: "var(--background-primary)",
                                    color: 'var(--text-primary)',
                                    border: '1px solid var(--borderColor)',
                                    borderRadius: '6px',
                                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15), 0 8px 30px rgba(0, 0, 0, 0.12)',
                                    padding: '8px',
                                    minWidth: '200px',
                                    marginTop: '10px',
                                },
                                '& .MuiTooltip-arrow': {
                                    color: "var(--text-primary)",
                                    '&::before': {
                                        border: '1px solid var(--borderColor)',
                                        backgroundColor: "var(--background-primary)",
                                        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
                                    },
                                },
                            },
                        }}
                        arrow
                    >
                        <div>
                            <DialogVariableTooltip
                                selectedVariable={selectedVariable || {}}
                                setVariableKey={setVariableKey}
                                setVariableValue={setVariableValue}
                                variableKey={variableKey || ''}
                                variableValue={variableValue || ''}
                            />
                        </div>
                    </Tooltip>
                </div>
            )}
        </div>
    );
});

TextFieldDynamicVariableTooltip.displayName = 'TextFieldDynamicVariableTooltip';
function mapStateToProps(state, ownProps) {
    return {
        ...ownProps,
    }
}
export default connect(mapStateToProps)(TextFieldDynamicVariableTooltip);